import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UniversalButtonData, UserInfo } from '@cobu/types'

export type AuthLanguage = 'en' | 'es'

// Define the auth state type (aligning with mobile conventions)
export interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  streamToken: string | null
  intercomJwt: string | null
  user: any | null
  buildingCode: string | null
  buildingDashboardConfig: Record<string, any>
  universalButtonData: any | null
  message: string | null
  error: string | null
  language: AuthLanguage
  nativePermissions: {
    camera: boolean
    library: boolean
    calendar: boolean
  }
  nativePermissionsProvided: boolean
  // Signed token carrying the resident's email/phone, handed back by the handshake
  // until they accept the terms (the backend doesn't store them before that). Sent to
  // /oneapp/accept-terms on consent. Readable, not encrypted — never log it.
  // Memory-only because the store has no persistence layer (store/index.ts is a plain
  // configureStore). If redux-persist is ever added for this slice, blacklist this
  // field, or the resident's email/phone lands in localStorage.
  pendingContactToken: string | null
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  token: null,
  streamToken: null,
  intercomJwt: null,
  user: null,
  buildingCode: null,
  buildingDashboardConfig: {},
  universalButtonData: null,
  message: null,
  error: null,
  language: 'en',
  nativePermissions: {
    camera: false,
    library: false,
    calendar: false,
  },
  nativePermissionsProvided: false,
  pendingContactToken: null,
}

// Helper to derive authentication state from token
const computeIsAuthenticated = (token: string | null): boolean => token !== null

// Create auth slice with reducers (matching mobile actions/signatures)
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setUniversalButtonData: (state, action: PayloadAction<UniversalButtonData | null>) => {
      state.universalButtonData = action.payload
    },
    setAuthFromToken: (
      state,
      action: PayloadAction<{ token: string; user: any; streamToken?: string; intercomJwt?: string }>
    ) => {
      state.token = action.payload.token
      state.user = action.payload.user
      state.streamToken = action.payload.streamToken || null
      state.intercomJwt = action.payload.intercomJwt || null
      state.isAuthenticated = computeIsAuthenticated(state.token)
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<any | null>) => {
      state.user = action.payload
    },
    // Update user data
    updateUser: (state, action: PayloadAction<Partial<UserInfo>>) => {
      state.user = action.payload;
    },

    // Update profile image
    updateProfileImage: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.profileImage = action.payload;
      }
    },
    updateNativePermission: (
      state,
      action: PayloadAction<{ permission: 'camera' | 'library' | 'calendar'; granted: boolean }>
    ) => {
      state.nativePermissions[action.payload.permission] = action.payload.granted
    },
    setNativePermissionsProvided: (state, action: PayloadAction<boolean>) => {
      state.nativePermissionsProvided = action.payload
    },
    setLanguage: (state, action: PayloadAction<AuthLanguage>) => {
      state.language = action.payload
    },
    setPendingContactToken: (state, action: PayloadAction<string | null>) => {
      state.pendingContactToken = action.payload
    },
    reset: () => initialState,
  },
})

export const {
  setLoading,
  setUniversalButtonData,
  setAuthFromToken,
  clearError,
  setUser,
  updateUser,
  updateProfileImage,
  updateNativePermission,
  setNativePermissionsProvided,
  setLanguage,
  setPendingContactToken,
  reset,
} = authSlice.actions

export default authSlice.reducer

// Selectors
export const selectIsAuthenticated = (state: { auth: { token: string | null } }) =>
  computeIsAuthenticated(state.auth.token)


